import { Link } from 'react-router'
import { useBahasa } from '../../../app/i18n'
import {
  useJadwal, useOrangSaya, usePagu, usePengetahuanSaya, useRingkasanSaya,
} from '../../../domains/tenancy/api'
import Fitur from './Fitur'
import { uangUSD } from '../../../shared/lib/waktu'
import { Angka, Gagal, KepalaHalaman, Kosong, Memuat, Panel } from '../../../shared/ui'

/**
 * BERANDA — daftar TUGAS, bukan dasbor angka.
 *
 * `DESAIN_BACKOFFICE.md`: *"Admin tenant mendarat di daftar tugas karena ia membuka
 * aplikasi ini seminggu sekali dan yang ia butuhkan cuma satu: apa yang tertahan
 * menunggu orang."*
 *
 * Jadi isinya bukan grafik. Tiap baris menyebut satu hal yang belum beres DAN tautan ke
 * tempat menyelesaikannya. Yang sudah beres tidak didaftar — daftar tugas yang memuat
 * yang sudah selesai berhenti dibaca.
 */
export default function Beranda() {
  const { t, locale } = useBahasa()
  const jadwal = useJadwal()
  const peng = usePengetahuanSaya()
  const orang = useOrangSaya()

  // Kueri fitur TIDAK ikut digerbangi di sini — `<Fitur />` di bawah mengurus muat dan
  // galatnya sendiri. Menggerbanginya di sini berarti satu kueri yang gagal menyembunyikan
  // SELURUH daftar tugas, termasuk yang datanya sudah ada dan masih bisa dikerjakan.
  if (jadwal.isLoading || peng.isLoading) return <Memuat tinggi={5} />
  if (jadwal.isError) return <Gagal galat={jadwal.error} ulangi={jadwal.refetch} />

  const tugas = []

  // Pengetahuan kosong: bot akan menjawab tanpa bahan. Ini yang paling mahal kalau
  // dibiarkan — BUGS VIII-1 menunjukkan bot tetap menjawab dengan percaya diri.
  const dokAktif = (peng.data ?? []).filter((d) => d.is_active).length
  if (dokAktif === 0) {
    tugas.push({ ke: '/pengetahuan', judul: t('ta.tugasPengetahuan'),
                 ket: t('ta.tugasPengetahuanKet') })
  }

  // TUGAS "fitur mati" DICABUT 11 September, dan bukan karena tidak berguna.
  //
  // Daftar tugas ini berisi hal yang admin tenant BISA kerjakan sendiri. Sejak ADR-067,
  // menyalakan fitur adalah wewenang operator Smova — jadi baris "3 fitur mati" yang
  // menautkan ke halaman pengaturan menjanjikan tindakan yang tidak ada tombolnya.
  //
  // Yang menggantikannya: daftar fiturnya sendiri, baca-saja, tepat di bawah. Ia menjawab
  // pertanyaan yang sama ("apa yang saya punya, apa yang mati") tanpa berpura-pura bisa
  // mengubahnya.

  const belumJadwal = (jadwal.data ?? []).filter((j) => j.mode === 'always')
  if (belumJadwal.length === (jadwal.data ?? []).length && belumJadwal.length > 0) {
    tugas.push({ ke: '/chatbot', judul: t('ta.tugasJadwal'), ket: t('ta.tugasJadwalKet') })
  }

  // ORANG YANG BELUM /join TIDAK MENERIMA NOTIFIKASI APA PUN, dan tombol di notifikasi
  // grup menolaknya. Ini satu-satunya tugas di rancangan yang jelas milik admin —
  // dua lainnya ("DM belum ditangani", "konsep menunggu persetujuan") menangani
  // pelanggan, dan itu pekerjaan staf di shell yang lain.
  //
  // Kueri orangnya TIDAK digerbangi di atas: Shell sudah memuatnya untuk lencana menu,
  // jadi ini membaca cache yang sama dan nol permintaan baru. Yang gagal menghasilkan
  // daftar kosong, dan tugas ini sekadar tidak muncul — bukan menyembunyikan yang lain.
  const belumTaut = (orang.data ?? []).filter((u) => !u.telegram_user_id)
  if (belumTaut.length > 0) {
    tugas.push({ ke: '/tim', judul: t('ta.tugasTelegram', { n: belumTaut.length }),
                 ket: t('ta.tugasTelegramKet') })
  }

  return (
    <>
      <KepalaHalaman judul={t('ta.navBeranda')} keterangan={t('ta.berandaCatatan')} />

      <Panel judul={t('ta.berandaJudul')}>
      {tugas.length === 0 ? (
        <Kosong judul={t('ta.tidakAdaTugas')} keterangan={t('ta.tidakAdaTugasKet')} />
      ) : (
        <ul className="m-0 list-none p-3.5 flex flex-col gap-2.5">
          {tugas.map((x) => (
            <li key={x.judul}>
              <Link
                to={x.ke}
                className="flex flex-col gap-1 no-underline bg-surface2 border border-line
                           border-l-[3px] border-l-accent rounded-md px-3.5 py-2.5
                           hover:border-l-ink"
              >
                <b className="text-[13px] text-ink">{x.judul}</b>
                <span className="text-[11.5px] text-ink2">{x.ket}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      </Panel>

      {/* DAFTAR FITUR, baca-saja. Dipindah dari tab di dalam "Chatbot" 11 September:
          isinya seluruh 12 fitur — scraping, publishing, meeting — jadi menaruhnya di
          dalam menu yang namanya satu fitur membuatnya tidak pernah ditemukan orang yang
          mencari "apa saja yang saya punya". Beranda adalah tempat pertanyaan itu
          diajukan. */}
      {/* ANGKA DI BAWAH TUGAS, dan itu urutan yang disengaja di rancangan: *"angka,
          bukan tugas — sengaja di bawah."* Admin tenant membuka aplikasi ini seminggu
          sekali; yang ia butuhkan lebih dulu adalah apa yang tertahan, bukan grafik. */}
      <TujuhHari t={t} locale={locale} />

      <Fitur />
    </>
  )
}

/**
 * TUJUH HARI TERAKHIR — pengawasan, bukan penanganan.
 *
 * Nol baris pelanggan, nol tombol, nol tautan ke percakapan: admin tenant MENGATUR, dan
 * menangani DM pekerjaan stafnya. Yang dijawab panel ini cuma "yang saya bayar ini jalan
 * atau tidak".
 *
 * TIAP ANGKA PUNYA PENDAMPING, dan itu yang membuatnya terbaca. "38 DM masuk" sendirian
 * tidak memberi tahu apa pun; "31 dibalas bot" memberi tahu botnya bekerja — dan dua
 * angka yang sama besar berarti bot menyerah pada semuanya.
 */
function TujuhHari({ t, locale }) {
  const q = useRingkasanSaya(7)
  const pagu = usePagu()

  // Panel ini TIDAK menggerbangi halaman. Ia di bawah daftar tugas, dan tugas yang
  // datanya sudah ada harus tetap terbaca walau rute ringkasan gagal.
  if (q.isLoading || q.isError) return null

  const per = Object.fromEntries((q.data?.angka ?? []).map((a) => [a.kunci, a]))
  const d = pagu.data
  const sisa = d && d.pagu_bulanan_usd != null
    ? Math.max(0, d.pagu_bulanan_usd - d.terpakai_usd)
    : null

  const baris = [
    ['dm', t('ta.angka.dm'), per.dm?.nilai, t('ta.angka.dmDari', { n: per.dm?.dari ?? 0 })],
    ['prospek', t('ta.angka.prospek'), per.prospek?.nilai,
     t('ta.angka.prospekDari', { n: per.prospek?.dari ?? 0 })],
    // SATU-SATUNYA angka di sini yang menuntut tindakan, jadi ia menyebut keadaan aman
    // secara eksplisit alih-alih menulis "0 belum ada peserta" yang terbaca seperti kabar
    // buruk. Peserta, bukan pemegang: pemegang meeting dicabut (revisi Telegram R7).
    ['meeting', t('ta.angka.meeting'), per.meeting?.nilai,
     per.meeting?.dari ? t('ta.angka.meetingDari', { n: per.meeting.dari })
                       : t('ta.angka.meetingAman')],
    ['konten', t('ta.angka.konten'), per.konten?.nilai,
     t('ta.angka.kontenDari', { n: per.konten?.dari ?? 0 })],
  ]

  return (
    <Panel judul={t('ta.tujuhHari', { n: q.data?.hari ?? 7 })} catatan={t('ta.tujuhCatatan')}>
      <div className="grid gap-px bg-line
                      [grid-template-columns:repeat(auto-fit,minmax(126px,1fr))]">
        {baris.map(([kunci, label, nilai, ket]) => (
          <div key={kunci} className="bg-surface px-3.5 py-3 flex flex-col gap-0.5">
            <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-ink3">
              {label}
            </span>
            <Angka nilai={nilai ?? 0} locale={locale} />
            <span className="text-[11px] text-ink2">{ket}</span>
          </div>
        ))}

        {/* SISA PAGU, bukan terpakai. Pertanyaan admin selalu "berapa lagi yang bisa
            saya pakai", dan tenant TANPA pagu tidak punya jawabannya — ia tidak
            dibatasi, dan "0%" untuknya kabar baik yang salah. */}
        <div className="bg-surface px-3.5 py-3 flex flex-col gap-0.5">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-ink3">
            {t('ta.angka.pagu')}
          </span>
          <b className="text-[19px] tabular leading-tight">
            {d?.pagu_bulanan_usd == null ? '—' : `${Math.max(0, 100 - (d.persen ?? 0))}%`}
          </b>
          <span className="text-[11px] text-ink2">
            {sisa == null
              ? t('ta.angka.tanpaPagu')
              : t('ta.angka.paguDari', { sisa: uangUSD(sisa, locale) })}
          </span>
        </div>
      </div>
    </Panel>
  )
}
